import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABEL } from "@/lib/api";
import { LogOut, Shield } from "lucide-react";
import DemoBanner from "@/components/DemoBanner";

function ThunderbirdMark({ size = 22 }) {
  // Indigenous Tipi glyph — triangular lodge with crossed poles and doorway
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="square" strokeLinejoin="miter" aria-label="tipi-mark">
      {/* Left pole extension above the tipi peak */}
      <line x1="14" y1="4" x2="10.5" y2="8" />
      {/* Right pole extension above the tipi peak */}
      <line x1="18" y1="4" x2="21.5" y2="8" />
      {/* Central pole extension */}
      <line x1="16" y1="3" x2="16" y2="7" />
      {/* Tipi cone — left side */}
      <line x1="16" y1="6" x2="4" y2="27" />
      {/* Tipi cone — right side */}
      <line x1="16" y1="6" x2="28" y2="27" />
      {/* Ground line */}
      <line x1="4" y1="27" x2="28" y2="27" />
      {/* Doorway triangle */}
      <polyline points="13,27 16,20 19,27" />
    </svg>
  );
}

const PUBLIC_LINKS = [
  { to: "/about", label: "01 · About" },
  { to: "/history", label: "02 · History" },
  { to: "/departments", label: "03 · Departments" },
  { to: "/careers", label: "04 · Careers" },
  { to: "/transparency", label: "05 · Ledger" },
  { to: "/contact", label: "06 · Contact" },
];

// Members subnav — grouped by function for visual order.
const MEMBER_NAV_GROUPS = [
  {
    name: null, // no header label — "home" section
    links: [
      { to: "/portal", label: "Dashboard" },
      { to: "/portal/me", label: "My Space" },
    ],
  },
  {
    name: "Consult",
    links: [
      { to: "/portal/polls", label: "Polls" },
      { to: "/portal/surveys", label: "Surveys" },
      { to: "/portal/discussions", label: "Threads" },
    ],
  },
  {
    name: "Operate",
    links: [
      { to: "/portal/sessions", label: "Sessions" },
      { to: "/portal/portfolios", label: "Portfolios" },
      { to: "/portal/funding", label: "Funding", gate: "funding" },
      { to: "/portal/archive", label: "Archive" },
    ],
  },
  {
    name: "Admin",
    gated: "admin",
    links: [
      { to: "/portal/reports", label: "Reports", gate: "report" },
      { to: "/portal/hrm",     label: "HR",      gate: "hr" },
    ],
  },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const isClerk = user && user.role === "band_registry_clerk";
  const COMMAND_ROLES = new Set(["chief", "deputy_chief", "head_councillor", "councillor", "executive_director"]);
  const REPORT_ROLES = new Set(["chief", "deputy_chief", "head_councillor", "councillor", "executive_director", "band_registry_clerk"]);
  const HR_VIEW_ROLES = new Set(["chief", "deputy_chief", "head_councillor", "councillor", "executive_director"]);
  const canCommand = user && user.status === "approved" && (user.is_super_admin || COMMAND_ROLES.has(user.role));
  const canReport = user && user.status === "approved" && (user.is_super_admin || user.is_owner || REPORT_ROLES.has(user.role));
  const canHR = user && user.status === "approved" && (user.is_super_admin || user.is_owner || HR_VIEW_ROLES.has(user.role));
  const canFunding = user && user.status === "approved"; // all approved members can view funding (transparency); write is server-gated

  const memberGroups = MEMBER_NAV_GROUPS
    .map(g => ({
      ...g,
      links: g.links.filter(l => {
        if (l.gate === "report") return canReport;
        if (l.gate === "hr") return canHR;
        if (l.gate === "funding") return canFunding;
        return true;
      })
    }))
    .filter(g => g.links.length > 0);

  return (
    <div className="min-h-screen bg-[color:var(--bg)] text-white flex flex-col">
      <DemoBanner />
      <header className="sticky top-0 z-40 border-b border-white/8 bg-[color:var(--bg)]/85 backdrop-blur-xl">
        <div className="max-w-[1400px] mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="brand-link">
            <div className="w-9 h-9 border border-[color:var(--cobalt)]/60 flex items-center justify-center text-[color:var(--cobalt)] hover:bg-[color:var(--cobalt)]/10 transition-colors">
              <ThunderbirdMark size={22} />
            </div>
            <div className="leading-tight">
              <div className="font-display text-lg tracking-tight">Kasabonika Lake</div>
              <div className="font-mono text-[10px] text-white/45 tracking-widest uppercase">Governance Portal</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {PUBLIC_LINKS.map((l) => (
              <NavLink key={l.to} to={l.to} className={({isActive})=>`px-3 py-2 font-mono text-[11px] uppercase tracking-widest border border-transparent ${isActive ? "text-[color:var(--cobalt)] border-[color:var(--cobalt)]/40" : "text-white/70 hover:text-white hover:border-white/15"}`} data-testid={`nav-${l.to.replace('/','')}`}>
                {l.label}
              </NavLink>
            ))}
            {user && user.status === "approved" && (
              <NavLink to="/portal" className={({isActive})=>`px-3 py-2 font-mono text-[11px] uppercase tracking-widest border ml-1 ${isActive ? "border-[color:var(--cobalt)] text-[color:var(--cobalt)] bg-[color:var(--cobalt)]/8" : "border-[color:var(--cobalt)]/40 text-[color:var(--cobalt)] hover:bg-[color:var(--cobalt)]/8"}`} data-testid="nav-portal">
                Members Portal
              </NavLink>
            )}
            {isClerk && (
              <NavLink to="/portal/clerk" className={({isActive})=>`px-3 py-2 font-mono text-[11px] uppercase tracking-widest border ${isActive ? "border-[color:var(--signal)] text-[color:var(--signal)]" : "border-[color:var(--signal)]/40 text-[color:var(--signal)] hover:bg-[color:var(--signal)]/8"}`} data-testid="nav-clerk">
                <Shield size={12} className="inline mr-1" />Registry Console
              </NavLink>
            )}
          </nav>

          <div className="flex items-center gap-2">
            {user ? (
              <>
                <div className="hidden md:block text-right">
                  <div className="font-mono text-[10px] text-white/40 uppercase tracking-widest">
                    {ROLE_LABEL[user.role] || user.role} · {user.status?.toUpperCase()}
                  </div>
                  <div className="font-mono text-xs text-white/80">{user.first_name} {user.last_name}</div>
                </div>
                <button onClick={async ()=>{ await logout(); nav("/"); }} className="btn-cta" data-testid="logout-btn">
                  <LogOut size={12} /> Sign Out
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="btn-cta" data-testid="header-login-btn">Sign in</Link>
                <Link to="/register" className="btn-cta primary" data-testid="header-register-btn">Request access</Link>
              </>
            )}
          </div>
        </div>
        {user && user.status === "approved" && (
          <div className="border-t border-white/8" style={{ background: "rgba(17,17,27,0.45)", backdropFilter: "blur(12px)" }}>
            <div className="max-w-[1400px] mx-auto px-6 h-10 flex items-center gap-0.5 overflow-x-auto">
              {memberGroups.map((g, gi) => (
                <div key={g.name || "home"} className="flex items-center gap-0.5">
                  {gi > 0 && <span className="mx-3 h-4 w-px" style={{ background: "var(--border-hi)" }} aria-hidden />}
                  {g.name && (
                    <span className="font-mono text-[9px] uppercase tracking-[0.22em] mr-2" style={{ color: "var(--text-mute)" }}>
                      {g.name} ·
                    </span>
                  )}
                  {g.links.map(l => (
                    <NavLink
                      key={l.to} to={l.to} end={l.to === "/portal"}
                      className={({isActive})=>`px-2.5 py-1.5 font-mono text-[10.5px] uppercase tracking-widest border ${isActive ? "border-[color:var(--blue)] text-[color:var(--blue)]" : "border-transparent text-white/60 hover:text-white"}`}
                      data-testid={`subnav-${l.to.split('/').pop() || 'dashboard'}`}
                    >
                      {l.label}
                    </NavLink>
                  ))}
                </div>
              ))}
              {canCommand && (
                <NavLink
                  to="/portal/command"
                  className={({isActive})=>`px-3 py-1.5 font-mono text-[10.5px] uppercase tracking-widest border ml-auto ${isActive ? "border-[color:var(--mauve)] text-[color:var(--mauve)] bg-[color:var(--mauve)]/10" : "border-[color:var(--mauve)]/50 text-[color:var(--mauve)] hover:bg-[color:var(--mauve)]/8"}`}
                  data-testid="subnav-command"
                >
                  Command Center
                </NavLink>
              )}
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      <footer className="border-t border-white/8 bg-black/40">
        <div className="max-w-[1400px] mx-auto px-6 py-8 grid grid-cols-1 md:grid-cols-4 gap-6 text-xs">
          <div>
            <div className="label-mono mb-2">Location</div>
            <div className="text-white/70">Kasabonika Lake, ON · P0V 1Y0</div>
          </div>
          <div>
            <div className="label-mono mb-2">Jurisdiction</div>
            <div className="text-white/70">Treaty 9 · Nishnawbe-Aski Nation</div>
          </div>
          <div>
            <div className="label-mono mb-2">Contact</div>
            <div className="text-white/70">(807) 535-2547 · Fx (807) 535-1152</div>
          </div>
          <div>
            <div className="label-mono mb-2">Data Sovereignty</div>
            <div className="text-white/70">OCAP® Principles · Nation-owned record</div>
          </div>
        </div>
        <div className="border-t border-white/8 py-4 text-center font-mono text-[10px] text-white/35 uppercase tracking-widest">
          © 2026 Kasabonika Lake First Nation · Miigwech
        </div>
      </footer>
    </div>
  );
}
